import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Send, FileText, Loader2, Trash2, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ContingencyForm {
  id: string;
  title: string;
  form_type: string;
  status: string;
  sent_to_email: string | null;
  sent_to_name: string | null;
  sent_date: string | null;
  response_date: string | null;
  notes: string | null;
  created_at: string;
}

interface ContingencyFormsTabProps {
  jobId: string;
  orgId: string;
}

export const ContingencyFormsTab = ({ jobId, orgId }: ContingencyFormsTabProps) => {
  const [forms, setForms] = useState<ContingencyForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    form_type: "standard",
    sent_to_email: "",
    sent_to_name: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadContingencyForms();
  }, [jobId]);

  const loadContingencyForms = async () => {
    try {
      const { data, error } = await supabase
        .from("contingency_forms")
        .select("*")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setForms(data || []);
    } catch (error: any) {
      console.error("Error loading contingency forms:", error);
      toast.error("Failed to load contingency forms");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = async () => {
    if (!formData.title.trim()) {
      toast.error("Please enter a form title");
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from("contingency_forms").insert({
        job_id: jobId,
        org_id: orgId,
        title: formData.title,
        form_type: formData.form_type,
        sent_to_email: formData.sent_to_email || null,
        sent_to_name: formData.sent_to_name || null,
        notes: formData.notes || null,
        status: "draft",
      });

      if (error) throw error;

      toast.success("Contingency form created");
      setDialogOpen(false);
      setFormData({
        title: "",
        form_type: "standard",
        sent_to_email: "",
        sent_to_name: "",
        notes: "",
      });
      loadContingencyForms();
    } catch (error: any) {
      console.error("Error creating form:", error);
      toast.error("Failed to create form");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteForm = async (id: string) => {
    try {
      const { error } = await supabase
        .from("contingency_forms")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("Form deleted");
      loadContingencyForms();
    } catch (error: any) {
      console.error("Error deleting form:", error);
      toast.error("Failed to delete form");
    }
  };

  const handleSendForm = async (id: string) => {
    try {
      const { error } = await supabase
        .from("contingency_forms")
        .update({
          status: "sent",
          sent_date: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;
      toast.success("Form marked as sent");
      loadContingencyForms();
    } catch (error: any) {
      console.error("Error sending form:", error);
      toast.error("Failed to update form");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "draft":
        return "bg-muted";
      case "sent":
        return "bg-blue-500/10 text-blue-700 dark:text-blue-300";
      case "pending":
        return "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300";
      case "approved":
        return "bg-green-500/10 text-green-700 dark:text-green-300";
      case "rejected":
        return "bg-destructive/10 text-destructive";
      default:
        return "bg-muted";
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Contingency Forms</h2>
          <p className="text-muted-foreground mt-1">
            Manage and send contingency forms to insurance adjusters
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Create Form
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Contingency Form</DialogTitle>
              <DialogDescription>
                Create a new contingency form to send to the insurance company
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Form Title</Label>
                <Input
                  id="title"
                  placeholder="e.g., Additional Materials Required"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="form_type">Form Type</Label>
                <Select
                  value={formData.form_type}
                  onValueChange={(value) =>
                    setFormData({ ...formData, form_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sent_to_name">Recipient Name</Label>
                  <Input
                    id="sent_to_name"
                    placeholder="Adjuster name"
                    value={formData.sent_to_name}
                    onChange={(e) =>
                      setFormData({ ...formData, sent_to_name: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sent_to_email">Recipient Email</Label>
                  <Input
                    id="sent_to_email"
                    type="email"
                    placeholder="adjuster@insurance.com"
                    value={formData.sent_to_email}
                    onChange={(e) =>
                      setFormData({ ...formData, sent_to_email: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Additional notes or details..."
                  rows={4}
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button onClick={handleCreateForm} disabled={submitting}>
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Create Form
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Forms List */}
      <div className="grid gap-4">
        {forms.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">
                No contingency forms yet. Create one to get started.
              </p>
            </CardContent>
          </Card>
        ) : (
          forms.map((form) => (
            <Card key={form.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <CardTitle className="text-lg">{form.title}</CardTitle>
                      <Badge className={getStatusColor(form.status)}>
                        {form.status}
                      </Badge>
                    </div>
                    <CardDescription className="mt-2">
                      {form.sent_to_name && (
                        <span>
                          To: {form.sent_to_name}
                          {form.sent_to_email && ` (${form.sent_to_email})`}
                        </span>
                      )}
                      {form.sent_date && (
                        <span className="ml-4">
                          Sent: {new Date(form.sent_date).toLocaleDateString()}
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {form.status === "draft" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSendForm(form.id)}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Send
                      </Button>
                    )}
                    <Button size="sm" variant="outline">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteForm(form.id)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {form.notes && (
                <CardContent>
                  <p className="text-sm text-muted-foreground">{form.notes}</p>
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
